import {
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { kspExpression } from "@/components/tools/chem-reference/solubility-check";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import { decompose } from "@/lib/tool-kit/chem-reference/ions";
import {
  findTabulated,
  formulaMass,
  ionProduct,
  pureWaterSolubility,
  solubility,
  TABULATED,
} from "@/lib/tool-kit/chem-reference/ksp";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseFormula } from "@/lib/tool-kit/chemistry/formula";

export interface KspStrings {
  emptyPrompt: string;
  formulaError: string;
  notSalt: string;
  kspNeeded: string;
  kspInvalid: string;
  concInvalid: string;
  kspUsed: string;
  kspCustom: string;
  expressionLine: string;
  molarWater: string;
  gramsWater: string;
  molarCommon: string;
  gramsCommon: string;
  reduction: string;
  ionsAtEquilibrium: string;
  initialQ: string;
  alreadySaturated: string;
  working: string;
  workWater: string;
  workCommon: string;
  tableHeading: string;
  columnCompound: string;
  columnKsp: string;
  columnMolar: string;
  columnGrams: string;
}

export interface KspInputs {
  formula: string;
  ksp: number | null;
  cationInit: number | null;
  anionInit: number | null;
  sig: number;
  /** Names of fields that held unreadable text. */
  badKsp: boolean;
  badConc: boolean;
}

const STATE_SUFFIX = /\s*\((?:aq|s|l|g)\)\s*$/;

export function KspResults({
  inputs,
  strings,
  fmt,
}: {
  inputs: KspInputs;
  strings: KspStrings;
  fmt: NumberFormatter;
}) {
  const { formula, sig } = inputs;
  if (formula.trim() === "") {
    return <Notice>{strings.emptyPrompt}</Notice>;
  }
  if (!parseFormula(formula.replace(STATE_SUFFIX, "")).ok) {
    return <ErrorNotice>{strings.formulaError}</ErrorNotice>;
  }
  const salt = decompose(formula);
  if (!salt) {
    return <ErrorNotice>{strings.notSalt}</ErrorNotice>;
  }
  if (inputs.badKsp) {
    return <ErrorNotice>{strings.kspInvalid}</ErrorNotice>;
  }
  if (inputs.badConc) {
    return <ErrorNotice>{strings.concInvalid}</ErrorNotice>;
  }
  const tabulated = findTabulated(formula);
  const ksp = inputs.ksp ?? tabulated?.ksp ?? null;
  if (ksp === null) {
    return <Notice>{strings.kspNeeded}</Notice>;
  }
  if (!(ksp > 0)) {
    return <ErrorNotice>{strings.kspInvalid}</ErrorNotice>;
  }
  const { nCation: m, nAnion: n } = salt;
  const mass = formulaMass(tabulated?.formula ?? formula);
  const water = pureWaterSolubility(ksp, m, n);
  const cationInit = inputs.cationInit ?? 0;
  const anionInit = inputs.anionInit ?? 0;
  const common = cationInit > 0 || anionInit > 0;
  const result = solubility(ksp, m, n, cationInit, anionInit);
  const show = (value: number) => formatSig(fmt, value, sig);
  const grams = (value: number) => (mass === null ? "—" : show(value * mass));
  const q0 = ionProduct(cationInit, anionInit, m, n);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.molarWater}
          value={`${show(water)} mol/L`}
        />
        <StatCard label={strings.gramsWater} value={`${grams(water)} g/L`} />
        {common ? (
          <StatCard
            label={strings.molarCommon}
            value={`${show(result.molar)} mol/L`}
          />
        ) : null}
        {common ? (
          <StatCard
            label={strings.gramsCommon}
            value={`${grams(result.molar)} g/L`}
          />
        ) : null}
      </StatGrid>
      {common && !result.saturatedAlready && water > 0 ? (
        <Notice>
          {t(strings.reduction, {
            factor: formatSig(fmt, water / result.molar, 2),
          })}
        </Notice>
      ) : null}
      {common && result.saturatedAlready ? (
        <Notice>
          {t(strings.alreadySaturated, { q: show(q0), ksp: show(ksp) })}
        </Notice>
      ) : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.expressionLine, {
            expression: kspExpression(salt),
            ksp: show(ksp),
          })}
        </p>
        <p>
          {t(strings.workWater, {
            m: String(m),
            n: String(n),
            total: String(m + n),
            denominator: String(m ** m * n ** n),
            ksp: show(ksp),
            s: show(water),
          })}
        </p>
        {common ? (
          <p>
            {t(strings.workCommon, {
              cation: show(result.cation),
              anion: show(result.anion),
              s: show(result.molar),
            })}
          </p>
        ) : null}
        <p>{inputs.ksp === null ? strings.kspUsed : strings.kspCustom}</p>
      </div>
    </>
  );
}

export function KspTable({
  strings,
  fmt,
  sig,
}: {
  strings: KspStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const rows = TABULATED.map((entry) => {
    const s = pureWaterSolubility(
      entry.ksp,
      entry.salt.nCation,
      entry.salt.nAnion
    );
    const mass = formulaMass(entry.formula);
    return [
      entry.text,
      formatSig(fmt, entry.ksp, 2),
      formatSig(fmt, s, sig),
      mass === null ? "—" : formatSig(fmt, s * mass, sig),
    ];
  });
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <div className="max-h-96 overflow-auto">
        <DataTable
          head={[
            strings.columnCompound,
            strings.columnKsp,
            strings.columnMolar,
            strings.columnGrams,
          ]}
          rows={rows}
        />
      </div>
    </div>
  );
}
